import { Injectable } from '@angular/core'
import { ActiveCartFacade } from '@spartacus/cart/base/root'
import { AuthService } from '@spartacus/core'
import { combineLatest, Observable, of, ReplaySubject } from 'rxjs'
import { map, mergeMap, share, switchMap, take } from 'rxjs/operators'
import bufferDebounceTime from '../../../../util/buffer-debounce-time'
import { EbProductPrice, EbProductPriceItem } from '../../interfaces/eb-product.interface'
import { EbPricesAdapter } from '../connectors/eb-prices.adapter'

@Injectable()
export class EbPricesService {
  constructor(
    private readonly pricesAdapter: EbPricesAdapter,
    private readonly authService: AuthService,
    protected activeCartService: ActiveCartFacade
  ) {}

  requestProductPriceSubject = new ReplaySubject<{
    product: EbProductPriceItem
  }>(1)
  singlePriceFetcher$ = this.requestProductPriceSubject.pipe(
    bufferDebounceTime(500),
    mergeMap(tuples => {
      const uniqueProductList = []
      tuples.forEach(el => {
        const duplicateIndex = uniqueProductList.findIndex(
          ele => el.product.product === ele.product
        )
        if (duplicateIndex === -1) {
          uniqueProductList.push(el.product)
        } else {
          uniqueProductList[duplicateIndex] = el.product
        }
      })
      return this.pricesAdapter.fetchPrices(uniqueProductList as EbProductPriceItem[])
    }),
    share()
  )

  fetchPrice(priceItem: EbProductPriceItem): Observable<EbProductPrice> {
    return combineLatest([
      this.authService.isUserLoggedIn()
    ]).pipe(
      take(1),
      switchMap(([isLoggedIn]) => {
        if (isLoggedIn) {
          this.requestProductPriceSubject.next({ product: priceItem })
          return this.singlePriceFetcher$
        } else {
          return of({} as EbProductPrice)
        }
      }),
      map(productPrice => {
        return {
          ...productPrice,
          items: (productPrice?.items ?? []).filter(item => item.product === priceItem.product)
        }
      })
    )
  }

  fetchPrices(products: EbProductPriceItem[]): Observable<EbProductPrice> {
    return combineLatest([
      this.authService.isUserLoggedIn()
    ]).pipe(
      take(1),
      switchMap(([isLoggedIn]) => {
        if (isLoggedIn) {
          return this.pricesAdapter.fetchPrices(products)
        } else {
          return of({} as EbProductPrice)
        }
      })
    )
  }
}
